import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';
import { ErrorHandler, provideAppInitializer, inject, importProvidersFrom } from '@angular/core';
import { FeatureFlagsService } from './app/feature-flags.service';
import { NGXLogger, LoggerModule } from 'ngx-logger';
import { Router, withEnabledBlockingInitialNavigation, provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FeatureFlagContextService } from './app/feature-flag-context.service';
import { HttpConfigInterceptor } from './app/http-config.interceptor';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { appRoutes } from './app/app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { OpenFeatureModule, BooleanFeatureFlagDirective, StringFeatureFlagDirective } from '@openfeature/angular-sdk';
import { growthBookClientProvider } from './app/growth-book.provider';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { AppComponent } from './app/app.component';
import { IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';

Sentry.init({
  dsn: "https://232f902aac2341a58f0bef9f2b8d3062@o4508791806754816.ingest.us.sentry.io/4508791833296896",
  environment: environment.sentryEnvironment,
  release: ((window as unknown as Record<string, unknown>)['__SENTRY_RELEASE__'] as string | undefined) || `tea-shop-fe@${environment.version || 'unknown'}`,
  dist: ((window as unknown as Record<string, unknown>)['__SENTRY_DIST__'] as string | undefined) || environment.sentryEnvironment,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost", 
    /^https:\/\/sencha-tea\.com\/api/,
    /^https:\/\/libertea.sencha-tea\.com\/api/,
  ],
  // Session Replay
  // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(LoggerModule.forRoot({
            level: environment.LOGGER_LEVEL,
        }), ReactiveFormsModule, BrowserModule, OpenFeatureModule.forRoot({
            provider: growthBookClientProvider
        }), BooleanFeatureFlagDirective, StringFeatureFlagDirective, ...(environment.OPTIMISATION_GA) ? [
            NgxGoogleAnalyticsModule.forRoot(environment.OPTIMISATION_GA),
            NgxGoogleAnalyticsRouterModule,
        ] : []),
        {
            provide: ErrorHandler,
            useFactory: async (featureFlags: FeatureFlagsService, logger: NGXLogger) => {
                await featureFlags.isReady();
                const disableSentry = await featureFlags.getBooleanValue('disable-sentry-fe', false);
                if (disableSentry) {
                    logger.warn('Sentry disabled - using default error handler');
                    // Return a default ErrorHandler instead of null
                    return new class extends ErrorHandler {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        override handleError(error: any): void {
                            super.handleError(error);
                        }
                    }();
                }
                logger.log('Sentry enabled');
                const disableSentryDialog = await featureFlags.getBooleanValue('disable-sentry-dialog', false);
                if (disableSentryDialog) {
                    logger.warn('Sentry dialog disabled');
                }
                else {
                    logger.log('Sentry dialog enabled');
                }
                return Sentry.createErrorHandler({
                    showDialog: !disableSentryDialog,
                });
            },
            deps: [FeatureFlagsService, NGXLogger]
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: IMAGE_LOADER,
            useValue: (config: ImageLoaderConfig) => {
                // For Cloudflare Images, we need to support the transformed URLs
                // The URL might already be transformed by the imageModifier pipe
                if (config.src.includes('/cdn-cgi/image/')) {
                    return config.src;
                }
                // Default behavior for non-Cloudflare URLs
                return config.src;
            },
        },
        FeatureFlagContextService,
        provideAppInitializer(async () => {
            const contextService = inject(FeatureFlagContextService);
            inject(Sentry.TraceService); // Initialize trace service
            const logger = inject(NGXLogger);
            if (!environment.OPTIMISATION_GA) {
                logger.warn('GA disabled');
            }
            await contextService['updateContext']();
            return Promise.resolve();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpConfigInterceptor,
            multi: true,
        },
        provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
        provideAnimations(),
    ]
})
  .catch((err) => console.error(err));
